import { useCallback, useEffect, useState } from "react";
import { Check, RefreshCw, Smartphone, WifiOff, X, Minus } from "lucide-react";
import OfflineReadyBadge from "@/components/OfflineReadyBadge";


type CheckState = "pass" | "fail" | "unknown";

interface AutoCheck {
  id: string;
  label: string;
  detail: string;
  state: CheckState;
}

const MANUAL_KEY = "ios-pwa-checklist";

const manualSteps = [
  {
    id: "safari",
    label: "Open tradestoolbelt.com in Safari",
    detail: "iOS only allows Add to Home Screen from Safari — not Chrome or an in-app browser.",
  },
  {
    id: "share",
    label: "Tap Share → Add to Home Screen",
    detail: "The Share button is the square with an arrow in the Safari toolbar.",
  },
  {
    id: "launch",
    label: "Launch the app from the home-screen icon",
    detail: "It should open full-screen with no Safari address bar.",
  },
  {
    id: "browse",
    label: "Visit each trade hub once while online",
    detail: "Open Pipe, Electrical, Ductwork and Structural so every screen is cached.",
  },
  {
    id: "airplane",
    label: "Turn on Airplane Mode, then reopen the app",
    detail: "The offline badge should appear and every calculator should still run.",
  },
  {
    id: "verify",
    label: "Run one calculation and one reference table offline",
    detail: "Confirms both the code and the bundled data survived the cache.",
  },
];

const stateStyles: Record<CheckState, string> = {
  pass: "bg-primary/15 text-primary",
  fail: "bg-destructive/15 text-destructive",
  unknown: "bg-muted text-muted-foreground",
};

const OfflineChecklist = () => {
  const [checks, setChecks] = useState<AutoCheck[]>([]);
  const [done, setDone] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(MANUAL_KEY) || "[]");
    } catch {
      return [];
    }
  });
  const [scanning, setScanning] = useState(false);

  const runChecks = useCallback(async () => {
    setScanning(true);
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (navigator as unknown as { standalone?: boolean }).standalone === true;

    let swState: CheckState = "fail";
    let cacheState: CheckState = "fail";
    let cachedCount = 0;

    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      swState = regs.length > 0 ? "pass" : "fail";
    } else {
      swState = "unknown";
    }

    if ("caches" in window) {
      const names = await caches.keys();
      for (const name of names) {
        const keys = await (await caches.open(name)).keys();
        cachedCount += keys.length;
      }
      cacheState = cachedCount > 0 ? "pass" : "fail";
    } else {
      cacheState = "unknown";
    }

    setChecks([
      {
        id: "standalone",
        label: "Running as an installed app",
        detail: standalone
          ? "Launched from the home-screen icon — full screen mode."
          : "Currently open in a browser tab. Add to Home Screen, then re-run this check from the installed app.",
        state: standalone ? "pass" : "fail",
      },
      {
        id: "sw",
        label: "Offline engine active",
        detail:
          swState === "pass"
            ? "The offline cache worker is installed and serving this device."
            : swState === "unknown"
              ? "This browser doesn't report offline worker support."
              : "Not registered yet. Offline mode only activates on the live site (tradestoolbelt.com), not the editor preview.",
        state: swState,
      },
      {
        id: "cache",
        label: "App files cached on device",
        detail:
          cacheState === "pass"
            ? `${cachedCount} files stored locally — screens and data load with no signal.`
            : "Nothing cached yet. Browse the app once while online.",
        state: cacheState,
      },
      {
        id: "network",
        label: navigator.onLine ? "Currently online" : "Currently offline — and still running",
        detail: navigator.onLine
          ? "Flip on Airplane Mode and reload to prove offline mode."
          : "You're reading this with no connection. Offline mode is working.",
        state: navigator.onLine ? "unknown" : "pass",
      },
    ]);
    setScanning(false);
  }, []);

  useEffect(() => {
    void runChecks();
  }, [runChecks]);

  const toggle = (id: string) => {
    setDone((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      localStorage.setItem(MANUAL_KEY, JSON.stringify(next));
      return next;
    });
  };

  const Icon = ({ state }: { state: CheckState }) =>
    state === "pass" ? <Check className="w-3.5 h-3.5" /> : state === "fail" ? <X className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />;

  return (
    <section className="space-y-3" aria-labelledby="offline-checklist-heading">
      <div className="flex items-center justify-between gap-2">
        <h2
          id="offline-checklist-heading"
          className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2"
        >
          <Smartphone className="w-4 h-4" />
          iOS Install & Offline Check
        </h2>
        <button
          type="button"
          onClick={() => void runChecks()}
          disabled={scanning}
          className="flex items-center gap-1 rounded-md border border-border bg-secondary px-2 py-1 text-[11px] font-medium text-foreground active:scale-95 disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${scanning ? "animate-spin" : ""}`} />
          Re-check
        </button>
      </div>

      <div className="trade-card space-y-3">
        <OfflineReadyBadge />
        <p className="text-xs text-muted-foreground">
          Live status on this device — run it again from the installed app to confirm.
        </p>

        <ul className="space-y-2">
          {checks.map((c) => (
            <li key={c.id} className="flex items-start gap-2.5">
              <span
                className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${stateStyles[c.state]}`}
                aria-label={c.state}
              >
                <Icon state={c.state} />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{c.label}</p>
                <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">{c.detail}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="trade-card space-y-3">
        <div className="flex items-center gap-2">
          <WifiOff className="w-4 h-4 text-primary" />
          <p className="text-sm font-semibold text-foreground">
            Step-by-step ({done.length}/{manualSteps.length})
          </p>
        </div>
        <ul className="space-y-1 divide-y divide-border">
          {manualSteps.map((s) => {
            const checked = done.includes(s.id);
            return (
              <li key={s.id} className="pt-1 first:pt-0">
                <button
                  type="button"
                  onClick={() => toggle(s.id)}
                  aria-pressed={checked}
                  className="flex w-full items-start gap-2.5 py-2 text-left"
                >
                  <span
                    className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                      checked ? "border-primary bg-primary text-primary-foreground" : "border-border bg-secondary"
                    }`}
                  >
                    {checked && <Check className="w-3.5 h-3.5" />}
                  </span>
                  <span className="min-w-0">
                    <span
                      className={`block text-sm font-medium ${checked ? "text-muted-foreground line-through" : "text-foreground"}`}
                    >
                      {s.label}
                    </span>
                    <span className="block text-xs text-muted-foreground leading-relaxed mt-0.5">{s.detail}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

export default OfflineChecklist;
